import React from 'react';
import { Table } from 'antd';
import { type ColumnsType, type TablePaginationConfig, type TableProps } from 'antd/es/table';
import { type SortOrder } from 'antd/es/table/interface';
import numeral from 'numeral';
import dayjs from 'dayjs';
import { type FinancesTableRow, type FinancesFilters } from '../../../../types/finances';
import { FinancesSummary } from './FinancesSummary';
import { getColumnVisibility } from '../utils/columnUtils';
import { TruncatedCell } from './TruncatedCell';
import { toNumber } from '../../../../helpers/number';
import { getExtraColLabel } from '../utils/constants';

type OnTableChange = TableProps<FinancesTableRow>['onChange'];

interface FinancesTableProps {
  data: FinancesTableRow[];
  loading: boolean;
  filters: FinancesFilters;
  pagination: false | TablePaginationConfig;
  onTableChange?: OnTableChange;
  sortState: {
    by?: string;
    direction?: 'ASC' | 'DESC';
  };
}

export const FinancesTable: React.FC<FinancesTableProps> = ({
  data,
  loading,
  filters,
  pagination,
  onTableChange,
  sortState,
}) => {
  const getColumns = (): ColumnsType<FinancesTableRow> => {
    const columns: ColumnsType<FinancesTableRow> = [];
    const columnVisibility = getColumnVisibility(filters, data);

    const toAntSortOrder = (direction?: 'ASC' | 'DESC'): SortOrder | undefined => {
      if (!direction) return undefined;
      return direction === 'ASC' ? 'ascend' : 'descend';
    };

    const getSortOrderForColumn = (columnKey: string): SortOrder | undefined => {
      if (sortState.by !== columnKey) return undefined;
      return toAntSortOrder(sortState.direction);
    };

    columns.push({
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      align: 'right',
      render: (value: number) => value ?? '-',
    });

    // For values that are fractions in 0..1 (e.g., win rate)
    const formatFractionAsPercent = (value?: number) => {
      if (value === undefined || value === null) return '-';
      const v = value > 1 ? value / 100 : value;
      return numeral(v).format('0.00%');
    };

    // For values that are already percent numbers (e.g., coverage: 0.244 => 0.24%)
    const formatPercentNumber = (value?: number) => {
      if (value === undefined || value === null) return '-';
      const v = value > 100 ? 100 : value; // clamp improbable outliers
      // Render as a percent number without implicit x100
      return `${numeral(v).format('0.00')}%`;
    };

    const { hiddenByResponse } = columnVisibility;

    // Date column
    if (columnVisibility.dateColumn && !hiddenByResponse.has('datetime')) {
      columns.push({
        title: 'Date',
        dataIndex: 'datetime',
        key: 'datetime',
        render: (datetime: string) => {
          if (!datetime) return '-';

          if (filters.format === 'by_hour') {
            return dayjs(datetime).format('YYYY-MM-DD HH:mm');
          } else if (filters.format === 'by_day') {
            return dayjs(datetime).format('YYYY-MM-DD');
          }

          return datetime;
        },
        sortOrder: getSortOrderForColumn('datetime'),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
      });
    }

    // SSP column
    if (columnVisibility.sspColumn && !hiddenByResponse.has('sspId')) {
      columns.push({
        title: 'SSP',
        dataIndex: 'sspId',
        key: 'sspId',
        render: (sspId: string) => <TruncatedCell text={sspId || 'Unknown'} />,
        sorter: true,
        sortOrder: getSortOrderForColumn('sspId'),
        sortDirections: ['ascend', 'descend'],
      });
    }

    // DSP column
    if (columnVisibility.dspColumn && !hiddenByResponse.has('dspId')) {
      columns.push({
        title: 'DSP',
        dataIndex: 'dspId',
        key: 'dspId',
        render: (dspId: string) => <TruncatedCell text={dspId || 'Unknown'} />,
        sorter: true,
        sortOrder: getSortOrderForColumn('dspId'),
        sortDirections: ['ascend', 'descend'],
      });
    }

    // Dynamic groupBy columns
    columnVisibility.dynamicGroupByColumns.forEach((field) => {
      if (hiddenByResponse.has(field)) return;

      const prettyTitles: Record<string, string> = {
        companyId: 'Company',
      };

      columns.push({
        title: prettyTitles[field] || field.charAt(0).toUpperCase() + field.slice(1),
        dataIndex: field,
        key: field,
        render: (value: string | number) => <TruncatedCell text={String(value || '-')} />,
        sorter: true,
        sortOrder: getSortOrderForColumn(field),
        sortDirections: ['ascend', 'descend'],
      });
    });

    // Core metric columns (present unless the API dropped them)
    if (!hiddenByResponse.has('sspRequests')) {
      columns.push({
        title: 'SSP Requests',
        dataIndex: 'sspRequests',
        key: 'sspRequests',
        render: (value: number) => numeral(value || 0).format('0,0'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('sspRequests'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('dspRequests')) {
      columns.push({
        title: 'DSP Requests',
        dataIndex: 'dspRequests',
        key: 'dspRequests',
        render: (value: number) => numeral(value || 0).format('0,0'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('dspRequests'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('coverage')) {
      columns.push({
        title: 'Coverage',
        dataIndex: 'coverage',
        key: 'coverage',
        render: (value: number) => formatPercentNumber(value),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('coverage'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('winRate')) {
      columns.push({
        title: 'Win Rate',
        dataIndex: 'winRate',
        key: 'winRate',
        render: (value: number) => formatFractionAsPercent(value),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('winRate'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('bids')) {
      columns.push({
        title: 'Bids',
        dataIndex: 'bids',
        key: 'bids',
        render: (value: number) => numeral(value).format('0,0'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('bids'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('impressions')) {
      columns.push({
        title: 'Impressions',
        dataIndex: 'impressions',
        key: 'impressions',
        render: (value: number) => numeral(value).format('0,0'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('impressions'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('nurl')) {
      columns.push({
        title: 'NURL',
        dataIndex: 'nurl',
        key: 'nurl',
        render: (value: number) => numeral(value).format('0,0'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('nurl'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('burl')) {
      columns.push({
        title: 'BURL',
        dataIndex: 'burl',
        key: 'burl',
        render: (value: number) => numeral(value).format('0,0'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('burl'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('sspSpend')) {
      columns.push({
        title: 'SSP Spend',
        dataIndex: 'sspSpend',
        key: 'sspSpend',
        render: (value: number) => numeral(value).format('$0,0.000'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('sspSpend'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('dspSpend')) {
      columns.push({
        title: 'DSP Spend',
        dataIndex: 'dspSpend',
        key: 'dspSpend',
        render: (value: number) => numeral(value).format('$0,0.000'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('dspSpend'),
        sortDirections: ['ascend', 'descend'],
      });
    }
    if (!hiddenByResponse.has('profit')) {
      columns.push({
        title: 'Profit',
        dataIndex: 'profit',
        key: 'profit',
        render: (value: number) => numeral(value).format('$0,0.000'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn('profit'),
        sortDirections: ['ascend', 'descend'],
      });
    }

    // Extra columns selected via filters (after Profit)
    columnVisibility.extraColsColumns.forEach((field) => {
      if (hiddenByResponse.has(field)) return;

      columns.push({
        title: getExtraColLabel(field),
        dataIndex: field,
        key: field,
        render: (value: string | number) => numeral(toNumber(value)).format('0,0'),
        align: 'right',
        sorter: true,
        sortOrder: getSortOrderForColumn(field),
        sortDirections: ['ascend', 'descend'],
      });
    });

    // Ensure header titles can wrap to reduce min column width
    const headerWrappedColumns: ColumnsType<FinancesTableRow> = columns.map((col) => ({
      ...col,
      onHeaderCell: (column) => {
        const base = typeof col.onHeaderCell === 'function' ? col.onHeaderCell(column) : {};
        return {
          ...base,
          style: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(base as any)?.style,
            whiteSpace: 'normal',
            wordBreak: 'normal',
            overflowWrap: 'break-word',
            lineHeight: 1.1,
          },
        } as React.HTMLAttributes<HTMLElement>;
      },
    }));

    return headerWrappedColumns;
  };

  return (
    <Table<FinancesTableRow>
      size="small"
      pagination={pagination}
      dataSource={data}
      loading={loading}
      columns={getColumns()}
      rowKey="key"
      onChange={onTableChange}
      scroll={{ x: 'max-content' }}
      summary={() => <FinancesSummary data={data} filters={filters} />}
    />
  );
};
